// src/screens/DiscoverScreen.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import { borderRadius, shadows, transitions } from '../designTokens';
import Button from '../components/ui/Button';
import Tab from '../components/ui/Tab';
import Footer from '../components/Footer';
import { destinations as destinationsAPI } from '../api';
import LoadingState from '../components/ui/LoadingState';
import DestinationsMap from '../components/DestinationsMap';


function TrendingCard({ dest, onClick, delay }) {
  const img = dest.img || 'https://images.unsplash.com/photo-1488646953014-85cb44e28b71?w=800&q=80';

  return (
    <div
      role="button" tabIndex={0}
      aria-label={`View ${dest.name}`}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className="focus-ring"
      style={{
        position: 'relative', borderRadius: borderRadius.xl, overflow: 'hidden', cursor: 'pointer',
        height: 320, transition: `transform ${transitions.slow}, box-shadow ${transitions.slow}`,
        animation: 'fadeUp 0.6s ease both', animationDelay: `${delay}s`,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'; e.currentTarget.style.boxShadow = shadows.xl; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <img src={img} alt={dest.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: `transform ${transitions.slow}` }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)' }} />
      <div style={{ position: 'absolute', top: 16, left: 16 }}>
        <span style={{
          background: 'rgba(255,255,255,0.9)', borderRadius: borderRadius.pill, padding: '5px 14px',
          fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif",
        }}>
          {dest.tag?.charAt(0) || '\uD83D\uDCCD'} {dest.tag}
        </span>
      </div>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <span style={{
          background: B.amber, color: B.night, borderRadius: borderRadius.pill, padding: '5px 12px',
          fontSize: 11, fontWeight: 800, fontFamily: "'Montserrat',sans-serif",
        }}>
          \u2605 {dest.rating}
        </span>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 20px' }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: 'white', marginBottom: 6 }}>
          {dest.name}
        </h3>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginBottom: 10 }}>
          {dest.description}
        </p>
        <div style={{ display: 'flex', gap: 16, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
          <span>\uD83D\uDCC5 {dest.days} days</span>
          <span style={{ color: B.mint, fontWeight: 700 }}>
            {Number.isFinite(dest.budget) ? `from $${dest.budget.toLocaleString()}` : 'no price data'}
          </span>
        </div>
      </div>
    </div>
  );
}

function CollectionCard({ collection, onClick, delay }) {
  const img = collection.img || collection.destinations?.[0]?.img || 'https://images.unsplash.com/photo-1488646953014-85cb44e28b71?w=800&q=80';
  const color = collection.color || '#1a6b4a';

  return (
    <div
      role="button" tabIndex={0}
      aria-label={`View ${collection.title} collection`}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className="focus-ring"
      style={{
        position: 'relative', borderRadius: 18, overflow: 'hidden', cursor: 'pointer',
        height: 200, transition: `transform ${transitions.slow}`,
        animation: 'fadeUp 0.6s ease both', animationDelay: `${delay}s`,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <img src={img} alt={collection.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${color}dd, ${color}88)` }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 24 }}>
        <span style={{ fontSize: 36, marginBottom: 8 }}>{collection.emoji}</span>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: 'white', marginBottom: 4 }}>
          {collection.title}
        </h3>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
          {collection.subtitle}
        </p>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
          {collection.count} destinations
        </p>
      </div>
    </div>
  );
}

function ItineraryCard({ itinerary, goScreen, delay }) {
  const title = itinerary.title || itinerary.name || 'Trip';
  const destination = itinerary.destination || title;
  const img = itinerary.img || 'https://images.unsplash.com/photo-1488646953014-85cb44e28b71?w=800&q=80';
  const days = itinerary.days || itinerary.itineraryDays?.length || 3;
  const budget = itinerary.budget ?? 0;
  const author = itinerary.author || itinerary.user?.name || 'GreatBreak';
  const authorAvatar = author.split(' ').map(n => n[0]).join('').slice(0, 2);
  const highlights = itinerary.highlights || itinerary.itineraryDays?.slice(0, 3).map(d => d.title) || ['Explore', 'Adventure', 'Relax'];

  return (
    <div
      style={{
        background: 'white', borderRadius: 20, overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        animation: 'fadeUp 0.6s ease both', animationDelay: `${delay}s`,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'; }}
    >
      <div style={{ position: 'relative', height: 200 }}>
        <img src={img} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)' }} />
        <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16 }}>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: 'white', marginBottom: 4 }}>
            {title}
          </h3>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
            {destination}
          </p>
        </div>
      </div>
      <div style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: `linear-gradient(135deg, ${B.forest}, ${B.mint})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, color: 'white',
          }}>
            {authorAvatar}
          </div>
          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 600, color: B.night }}>
            {author}
          </span>
          <span style={{ marginLeft: 'auto', fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.muted }}>
            ❤️ {itinerary.likes || 0}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {(itinerary.tags || [itinerary.vibe || 'Travel']).map(tag => (
            <span key={tag} style={{
              background: `${B.forest}15`, color: B.forest, padding: '4px 10px',
              borderRadius: 50, fontSize: 11, fontWeight: 600,
              fontFamily: "'Montserrat',sans-serif",
            }}>
              {tag}
            </span>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: B.muted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            Highlights
          </p>
          {highlights.map((h, i) => (
            <p key={i} style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: '#666', marginBottom: 4 }}>
              • {h}
            </p>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${B.sand}`, paddingTop: 14 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted }}>
            📅 {days} days · <span style={{ color: B.forest, fontWeight: 700 }}>${budget.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => goScreen('userJourney')}
              style={{
                background: B.forest, color: 'white', border: 'none',
                padding: '8px 18px', borderRadius: 50, cursor: 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
                transition: 'all 0.2s',
              }}
            >
              + Customize
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DiscoverScreen({ goScreen }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('trending');
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [discoverData, setDiscoverData] = useState({ trending: [], collections: [], popular: [] });

  useEffect(() => {
    destinationsAPI.discover()
      .then(data => setDiscoverData(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const tabs = [
    { id: 'trending', label: `🔥 ${t('discover.trending')}`, count: discoverData.trending.length },
    { id: 'collections', label: `📂 ${t('discover.collections')}`, count: discoverData.collections.length },
    { id: 'itineraries', label: `🗺️ ${t('discover.itineraries')}`, count: discoverData.popular.length },
    { id: 'map', label: `📍 ${t('discover.map')}` },
  ];

  const allMapDestinations = [
    ...discoverData.trending,
    ...discoverData.popular,
  ];

  return (
    <div>
      {/* Hero */}
      <div style={{
        background: B.night, padding: '56px clamp(16px, 4vw, 40px) 48px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div style={{ position: 'relative', maxWidth: 700 }}>
          <p style={{
            color: B.mint, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase',
            fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 12,
          }}>
            {t('discover.getInspired')}
          </p>
          <h1 style={{
            fontFamily: "'Playfair Display',serif", fontSize: 'clamp(32px, 4vw, 48px)',
            color: 'white', fontWeight: 700, lineHeight: 1.2, marginBottom: 16,
          }}>
            {t('discover.heroTitle')}
          </h1>
          <p style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 16, color: '#999',
            lineHeight: 1.6, maxWidth: 520,
          }}>
            Browse trending destinations, curated collections, and popular itineraries from our community of travelers.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        background: 'white', borderBottom: `1px solid ${B.sand}`,
        padding: '0 clamp(16px, 4vw, 40px)', position: 'sticky', top: 68, zIndex: 100,
      }}>
        <Tab
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          variant="underline"
        />
      </div>

      {/* Content */}
      <div style={{ padding: '40px clamp(16px, 4vw, 40px)', minHeight: '60vh' }}>

        {/* Trending */}
        {activeTab === 'trending' && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
                Trending Now
              </h2>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: B.muted }}>
                The hottest destinations travelers are booking right now
              </p>
            </div>
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 24 }}>
                {[1,2,3,4].map(i => <LoadingState key={i} variant="skeleton" />)}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 24 }}>
                {discoverData.trending.map((dest, i) => (
                  <TrendingCard key={dest.id} dest={dest} onClick={() => goScreen('home')} delay={i * 0.1} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Collections */}
        {activeTab === 'collections' && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
                Curated Collections
              </h2>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: B.muted }}>
                Themed destination groups hand-picked by our travel experts
              </p>
            </div>
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 20 }}>
                {[1,2,3].map(i => <LoadingState key={i} variant="skeleton" />)}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 20 }}>
                {discoverData.collections.map((col, i) => (
                  <CollectionCard key={col.id} collection={col} onClick={() => goScreen('home')} delay={i * 0.1} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Map */}
        {activeTab === 'map' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
                {t('discover.mapTitle')}
              </h2>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: B.muted }}>
                {t('discover.mapSubtitle')}
              </p>
            </div>
            <DestinationsMap destinations={allMapDestinations} height={480} />
          </div>
        )}

        {/* Popular Itineraries */}
        {activeTab === 'itineraries' && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
                Popular Itineraries
              </h2>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: B.muted }}>
                Browse trip plans from our community and customize them for your own adventure
              </p>
            </div>
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 24 }}>
                {[1,2,3].map(i => <LoadingState key={i} variant="skeleton" />)}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 24 }}>
                {discoverData.popular.map((it, i) => (
                  <ItineraryCard key={it.id} itinerary={it} goScreen={goScreen} delay={i * 0.1} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{
        background: B.night, padding: '56px clamp(16px, 4vw, 40px)', textAlign: 'center',
      }}>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, color: 'white', marginBottom: 14 }}>
          Can't find what you're looking for?
        </h2>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: '#888', marginBottom: 28, maxWidth: 480, margin: '0 auto 28px' }}>
          Let our AI travel assistant help you plan the perfect trip, tailored just for you.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="accent" size="lg" onClick={() => goScreen('chat')}>
            💬 Chat with AI Assistant
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => goScreen('ai')}
            style={{ border: `2px solid ${B.amber}`, color: B.amber, background: 'transparent' }}
          >
            🤖 Use AI Planner
          </Button>
        </div>
      </div>

      <Footer goScreen={goScreen} />
    </div>
  );
}
